import type { CrashPhase, LobbyCrash } from '@tg/shared';
import { Rocket } from '@/art/rocket';
import s from './lobby.module.css';

/** The curve reaches the design frame (head at 84% / 21%) after this long; later it stays there. */
const FULL_MS = 8400;

export interface FlightState {
  /** null: no round data */
  m: number | null;
  tMs: number;
  crashed: boolean;
  phase: CrashPhase | null;
  /** betting: ms until take-off */
  leftMs: number;
}

export const NO_FLIGHT: FlightState = { m: null, tMs: 0, crashed: false, phase: null, leftMs: 0 };

/**
 * Liftoff preview at server time `serverNow` from the live round (the same curve as the game:
 * m(t) = e^(growthPerMs·t), shown floored to cents). The crash point is known only after the crash.
 */
export function flightAt(c: LobbyCrash | null, serverNow: number): FlightState {
  if (!c) return NO_FLIGHT;
  if (c.phase === 'betting') return { m: 1, tMs: 0, crashed: false, phase: 'betting', leftMs: Math.max(0, c.bettingEndsAt - serverNow) };
  if (c.phase === 'crashed' && c.crashPoint !== null) {
    return { m: c.crashPoint, tMs: Math.log(c.crashPoint) / c.growthPerMs, crashed: true, phase: 'crashed', leftMs: 0 };
  }
  const t = Math.max(0, serverNow - (c.startedAt ?? serverNow));
  return { m: Math.floor(Math.exp(c.growthPerMs * t) * 100 + 1e-9) / 100, tMs: t, crashed: false, phase: 'flying', leftMs: 0 };
}

/** Curve + rocket head, geometry of design/lobby (viewBox 500×240, head at 84% / 21%). */
export function LiftoffCurve({ tMs, crashed, compact }: { tMs: number; crashed: boolean; compact?: boolean }) {
  const f = Math.min(1, Math.max(0, tMs / FULL_MS));
  const a = Math.pow(f, 1.6);
  const x = 420 * f;
  const y = 236 - 186 * a;
  const d = `M0 236 C ${(220 * f).toFixed(1)} ${(236 - 4 * a).toFixed(1)}, ${(340 * f).toFixed(1)} ${(236 - 56 * a).toFixed(1)}, ${x.toFixed(1)} ${y.toFixed(1)}`;
  const col = crashed ? 'var(--down)' : 'var(--up)';
  // heading from the end tangent of the Bezier (viewBox ≈ the chart's aspect), nose along the curve
  const heading = f > 0 ? Math.max(-60, Math.min(0, (Math.atan2(-130 * a, 80 * f) * 180) / Math.PI)) : 0;
  return (
    <>
      <svg className={s.curveSvg} viewBox="0 0 500 240" preserveAspectRatio="none" aria-hidden="true">
        <path d={`${d} L${x.toFixed(1)} 240 L0 240 Z`} fill={col} opacity={0.1} />
        <path d={d} fill="none" stroke={col} strokeWidth={2} vectorEffect="non-scaling-stroke" />
        {!compact && !crashed && (
          <line x1="0" y1={y} x2="500" y2={y} stroke={col} strokeDasharray="3 5" opacity={0.5} vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      <div
        className={compact ? s.headSm : s.head}
        style={{ left: `${(x / 500) * 100}%`, top: `${(y / 240) * 100}%`, rotate: crashed ? '0deg' : `${heading.toFixed(1)}deg` }}
        aria-hidden="true"
      >
        <Rocket crashed={crashed} size="100%" />
      </div>
    </>
  );
}
